import React from "react";
import { CustomLoading } from "components/CustomLoading";
import axios from "axios";
import toast from "react-hot-toast";
import { KTMScreen } from "ktm-ui";
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay, closestCenter, useSensor, useSensors, PointerSensor } from "@dnd-kit/core";
import { FileText } from "lucide-react";
import "./manuals.css";
import Sidebar from "./Sidebar";
import HomeView from "./HomeView";
import CategoryContentView from "./CategoryContentView";
import CategoryForm from "./CategoryForm";
import FolderForm from "./FolderForm";
import PageView from "./PageView";
import PageEditor from "./PageEditor";
import FolderView from "./FolderView";
import ProfileView from "./ProfileView";
import QuestionsView from "./QuestionsView";
import QuestionDetailView from "./QuestionDetailView";
import KnowledgeManagementView from "./KnowledgeManagementView";
import ArchivedView from "./ArchivedView";
import TemplatesView from "./TemplatesView";
import SearchDropdown from "./SearchDropdown";
import SearchView from "./SearchView";
import QuestionForm from "./QuestionForm";
import ConfirmModal from "./ConfirmModal";
import { movePage } from "./dnd-helpers";
import { reloadBookmarks } from "./useBookmarks";
import { Search } from "lucide-react";

function readExpandedSet(key: string): Set<string> {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? new Set(arr.filter((x: unknown): x is string => typeof x === "string")) : new Set();
    } catch { return new Set(); }
}

function writeExpandedSet(key: string, set: Set<string>): void {
    try { localStorage.setItem(key, JSON.stringify(Array.from(set))); } catch { /* ignore quota / disabled */ }
}

export function expandedCategoriesKey(userId: string): string { return `manuals:ui:${userId}:expandedCategories`; }
export function expandedFoldersKey(userId: string): string { return `manuals:ui:${userId}:expandedFolders`; }

const MANUALS_PATH = "/ops/manuals";

function serializeView(v: ViewState): string {
    switch (v.view) {
        case "home": return "";
        case "category": return `?v=category&c=${v.categoryId}`;
        case "folder": return `?v=folder&c=${v.categoryId}&f=${v.folderId}`;
        case "page": return `?v=page&c=${v.categoryId}&p=${v.pageId}`;
        case "questions": return "?v=questions";
        case "question": return `?v=question&qid=${v.questionId}`;
        case "drafts": return "?v=drafts";
        case "profile": return `?v=profile&u=${v.userId}`;
        case "km": return "?v=km";
        case "templates": return "?v=templates";
        case "archive": return "?v=archive";
        case "search": {
            const type = v.type && v.type !== "all" ? `&type=${v.type}` : "";
            const cats = v.cats && v.cats.length ? `&cats=${v.cats.join(",")}` : "";
            return `?v=search&q=${encodeURIComponent(v.query)}${type}${cats}`;
        }
        case "editing": return ""; // not routed
    }
}

function parseView(search: string): ViewState {
    const p = new URLSearchParams(search);
    const v = p.get("v");
    const c = p.get("c");
    const f = p.get("f");
    const pg = p.get("p");
    const q = p.get("q");
    const u = p.get("u");
    const qid = p.get("qid");
    switch (v) {
        case "category": return c ? { view: "category", categoryId: c } : { view: "home" };
        case "folder": return c && f ? { view: "folder", folderId: f, categoryId: c } : { view: "home" };
        case "page": return c && pg ? { view: "page", pageId: pg, categoryId: c } : { view: "home" };
        case "questions": return { view: "questions" };
        case "question": return qid ? { view: "question", questionId: qid } : { view: "questions" };
        case "drafts": return { view: "drafts" };
        case "profile": return u ? { view: "profile", userId: u } : { view: "home" };
        case "km": return { view: "km" };
        case "templates": return { view: "templates" };
        case "archive": return { view: "archive" };
        case "search": {
            if (!q) return { view: "home" };
            const type = p.get("type") || undefined;
            const catsRaw = p.get("cats");
            const cats = catsRaw ? catsRaw.split(",").filter(s => s.length > 0) : undefined;
            return { view: "search", query: q, type, cats };
        }
        default: return { view: "home" };
    }
}

function sameView(a: ViewState, b: ViewState): boolean {
    if (a.view !== b.view) return false;
    switch (a.view) {
        case "home":
        case "questions":
        case "drafts":
        case "km":
        case "templates":
        case "archive":
            return true;
        case "category": return a.categoryId === (b as any).categoryId;
        case "folder": return a.categoryId === (b as any).categoryId && a.folderId === (b as any).folderId;
        case "page": return a.categoryId === (b as any).categoryId && a.pageId === (b as any).pageId;
        case "question": return a.questionId === (b as any).questionId;
        case "profile": return a.userId === (b as any).userId;
        case "search": {
            const bb = b as Extract<ViewState, { view: "search" }>;
            return a.query === bb.query && (a.type || "") === (bb.type || "") && (a.cats || []).join(",") === (bb.cats || []).join(",");
        }
        case "editing": return false;
    }
}

interface Category {
    id: string;
    name: string;
    description: string | null;
    color: string | null;
    icon: string | null;
    sortMode: string;
    accessLevel: string;
    allowedRoles: string | null;
}

interface FolderTarget {
    id: string;
    categoryId: string;
    name: string;
    description: string | null;
    icon: string | null;
    color: string | null;
    sortMode: string;
    isPinned: boolean;
    sortOrder: number;
}

type ViewState =
    | { view: "home" }
    | { view: "category"; categoryId: string }
    | { view: "page"; pageId: string; categoryId: string }
    | { view: "editing"; page: any; categoryId: string; folderId: string | null; reviewProposalId?: string }
    | { view: "questions" }
    | { view: "question"; questionId: string }
    | { view: "drafts" }
    | { view: "profile"; userId: string }
    | { view: "km" }
    | { view: "templates" }
    | { view: "archive" }
    | { view: "search"; query: string; type?: string; cats?: string[] }
    | { view: "folder"; folderId: string; categoryId: string };

interface ManualsState {
    loading: boolean;
    userId: string | null;
    isAdmin: boolean;
    role: string | null;
    categories: Category[];
    expandedCategories: Set<string>;
    currentView: ViewState;
    searchQuery: string;
    categoryFormOpen: boolean;
    categoryFormTarget: Category | null;
    folderFormOpen: boolean;
    folderFormTarget: FolderTarget | null;
    folderFormCategoryId: string | null;
    confirmModal: { title: string; message: string; confirmLabel: string; danger: boolean; onConfirm: () => void } | null;
    treeVersion: number;
    activeDrag: { type: "page"; label: string } | null;
    searchOpen: boolean;
    askQuestionOpen: boolean;
}

export default KTMScreen(
    { name: "manuals" },
    class extends React.Component<any, ManualsState> {
        state: ManualsState = {
            loading: true,
            userId: null,
            isAdmin: false,
            role: null,
            categories: [],
            expandedCategories: new Set<string>(),
            currentView: { view: "home" },
            searchQuery: "",
            categoryFormOpen: false,
            categoryFormTarget: null,
            folderFormOpen: false,
            folderFormTarget: null,
            folderFormCategoryId: null,
            confirmModal: null,
            treeVersion: 0,
            activeDrag: null,
            searchOpen: false,
            askQuestionOpen: false,
        };
        private searchWrapRef = React.createRef<HTMLDivElement>();
        private searchOutsideHandler = (e: MouseEvent) => {
            if (!this.state.searchOpen) return;
            const wrap = this.searchWrapRef.current;
            if (wrap && !wrap.contains(e.target as Node)) {
                this.setState({ searchOpen: false });
            }
        };

        componentDidMount() {
            const initial = parseView(window.location.search);
            this.setState({
                currentView: initial,
                searchQuery: initial.view === "search" ? initial.query : "",
            });
            window.addEventListener("popstate", this.handlePopState);
            document.addEventListener("mousedown", this.searchOutsideHandler);
            this.fetchContext();
        }

        componentWillUnmount() {
            window.removeEventListener("popstate", this.handlePopState);
            document.removeEventListener("mousedown", this.searchOutsideHandler);
        }

        handlePopState = () => {
            const v = parseView(window.location.search);
            this.setState(prev => sameView(prev.currentView, v) ? null : { currentView: v });
        };

        pushUrl = (view: ViewState, opts?: { replace?: boolean }) => {
            if (view.view === "editing") return;
            const url = MANUALS_PATH + serializeView(view);
            const current = window.location.pathname + window.location.search;
            if (current === url) return;
            if (opts?.replace) window.history.replaceState(null, "", url);
            else window.history.pushState(null, "", url);
        };

        navigate = (view: ViewState, opts?: { replace?: boolean }) => {
            this.setState({ currentView: view });
            this.pushUrl(view, opts);
        };

        fetchContext() {
            Promise.all([
                axios.get("/api/manuals/context"),
                axios.get("/api/manuals/categories"),
            ])
                .then(([ctxRes, catRes]) => {
                    if (ctxRes.data.success && catRes.data.success) {
                        const userId = ctxRes.data.userId as string;
                        const persisted = readExpandedSet(expandedCategoriesKey(userId));
                        const knownIds = new Set<string>((catRes.data.items as Array<{ id: string }>).map(c => c.id));
                        const expandedCategories = new Set<string>(Array.from(persisted).filter(id => knownIds.has(id)));
                        this.setState({
                            userId,
                            isAdmin: ctxRes.data.isAdmin,
                            role: ctxRes.data.role,
                            categories: catRes.data.items,
                            expandedCategories,
                            loading: false,
                        });
                    } else {
                        toast.error("Failed to load manuals");
                        this.setState({ loading: false });
                    }
                })
                .catch(() => {
                    toast.error("Failed to load manuals");
                    this.setState({ loading: false });
                });
        }

        reloadCategories = () => {
            axios.get("/api/manuals/categories")
                .then(res => {
                    if (res.data.success) {
                        this.setState({ categories: res.data.items });
                    } else {
                        toast.error("Failed to reload categories");
                    }
                })
                .catch(() => {
                    toast.error("Failed to reload categories");
                });
        };

        bumpTreeVersion = () => {
            this.setState(prev => ({ treeVersion: prev.treeVersion + 1 }));
        };

        handleDragStart = (event: DragStartEvent) => {
            const a = event.active.data.current as { type: "page"; label?: string } | undefined;
            if (!a || a.type !== "page") return;
            this.setState({ activeDrag: { type: a.type, label: a.label || "" } });
        };

        handleDragEnd = async (event: DragEndEvent) => {
            this.setState({ activeDrag: null });
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const a = active.data.current as { type: "page"; categoryId: string } | undefined;
            const o = over.data.current as { type: "folder" | "category-root"; folderId?: string; categoryId: string } | undefined;
            if (!a || !o) return;
            if (a.categoryId !== o.categoryId) {
                toast.error("Cross-category moves aren't supported yet");
                return;
            }
            if (a.type !== "page") return;
            const ok = await movePage(active.id as string, o.type === "folder" ? o.folderId! : null);
            if (ok) this.bumpTreeVersion();
        };

        handleDragCancel = () => {
            this.setState({ activeDrag: null });
        };

        toggleCategory = (catId: string) => {
            this.setState(prev => {
                const next = new Set(prev.expandedCategories);
                if (next.has(catId)) next.delete(catId);
                else next.add(catId);
                if (prev.userId) writeExpandedSet(expandedCategoriesKey(prev.userId), next);
                return { expandedCategories: next };
            });
        };

        selectCategory = (catId: string) => {
            this.setState(prev => {
                const next = new Set(prev.expandedCategories);
                next.add(catId);
                return { expandedCategories: next };
            });
            this.navigate({ view: "category", categoryId: catId });
        };

        handleSearch = (e: React.FormEvent) => {
            e.preventDefault();
            const q = this.state.searchQuery.trim();
            if (q) {
                this.setState({ searchOpen: false });
                this.navigate({ view: "search", query: q });
            }
        };

        closeSearch = () => this.setState({ searchOpen: false });
        openSearch = () => this.setState({ searchOpen: true });

        // category form
        openCategoryForm = (cat: Category | null) => {
            this.setState({ categoryFormOpen: true, categoryFormTarget: cat });
        };

        closeCategoryForm = () => {
            this.setState({ categoryFormOpen: false, categoryFormTarget: null });
        };

        handleCategoryFormSaved = () => {
            this.closeCategoryForm();
            this.reloadCategories();
            this.bumpTreeVersion();
            reloadBookmarks();
        };

        handleDeleteCategory = (cat: Category) => {
            this.setState({
                confirmModal: {
                    title: "Delete category",
                    message: `Delete "${cat.name}" and all its contents? This cannot be undone.`,
                    confirmLabel: "Delete",
                    danger: true,
                    onConfirm: () => {
                        this.setState({ confirmModal: null });
                        axios.delete(`/api/manuals/categories/${cat.id}`)
                            .then(res => {
                                if (res.data.success) {
                                    toast.success(`Deleted "${cat.name}"`);
                                    this.reloadCategories();
                                    this.bumpTreeVersion();
                                    reloadBookmarks();
                                    if (
                                        this.state.currentView.view === "category" &&
                                        this.state.currentView.categoryId === cat.id
                                    ) {
                                        this.navigate({ view: "home" }, { replace: true });
                                    }
                                } else {
                                    toast.error(res.data.message || "Failed to delete");
                                }
                            })
                            .catch(() => toast.error("Failed to delete category"));
                    },
                },
            });
        };

        handleArchiveCategory = (cat: Category) => {
            this.setState({
                confirmModal: {
                    title: "Archive category",
                    message: `Archive "${cat.name}"? It will be hidden everywhere, but you can restore it from the Archive view.`,
                    confirmLabel: "Archive",
                    danger: false,
                    onConfirm: () => {
                        this.setState({ confirmModal: null });
                        axios.post(`/api/manuals/categories/${cat.id}/archive`)
                            .then(res => {
                                if (res.data.success) {
                                    toast.success(`Archived "${cat.name}"`);
                                    this.reloadCategories();
                                    this.bumpTreeVersion();
                                    if (
                                        this.state.currentView.view === "category" &&
                                        this.state.currentView.categoryId === cat.id
                                    ) {
                                        this.navigate({ view: "home" }, { replace: true });
                                    }
                                } else {
                                    toast.error(res.data.message || "Failed to archive");
                                }
                            })
                            .catch(() => toast.error("Failed to archive category"));
                    },
                },
            });
        };

        // folder form
        openFolderForm = (categoryId: string) => {
            this.setState({
                folderFormOpen: true,
                folderFormTarget: null,
                folderFormCategoryId: categoryId,
            });
        };

        openEditFolderForm = (folder: FolderTarget) => {
            this.setState({
                folderFormOpen: true,
                folderFormTarget: folder,
                folderFormCategoryId: folder.categoryId,
            });
        };

        closeFolderForm = () => {
            this.setState({
                folderFormOpen: false,
                folderFormTarget: null,
                folderFormCategoryId: null,
            });
        };

        handleFolderFormSaved = () => {
            this.closeFolderForm();
            this.bumpTreeVersion();
        };

        handleDeleteFolder = (folder: FolderTarget) => {
            this.setState({
                confirmModal: {
                    title: "Delete folder",
                    message: `Delete "${folder.name}" and all its contents? This cannot be undone.`,
                    confirmLabel: "Delete",
                    danger: true,
                    onConfirm: () => {
                        this.setState({ confirmModal: null });
                        axios.delete(`/api/manuals/folders/${folder.id}`)
                            .then(res => {
                                if (res.data.success) {
                                    toast.success(`Deleted "${folder.name}"`);
                                    this.bumpTreeVersion();
                                } else {
                                    toast.error(res.data.message || "Failed to delete");
                                }
                            })
                            .catch(() => toast.error("Failed to delete folder"));
                    },
                },
            });
        };

        // page editing — opens full-page inline editor
        handleCreatePage = (categoryId: string, folderId: string | null) => {
            this.navigate({ view: "editing", page: null, categoryId, folderId });
        };

        handleEditPage = (page: any) => {
            this.navigate({ view: "editing", page, categoryId: page.categoryId, folderId: page.folderId });
        };

        // "Review & apply" on an edit request: open the editor seeded with the suggested
        // body so the admin can review/adjust before saving. Publishing applies it and
        // (via reviewProposalId) auto-accepts the request without re-writing the body.
        handleReviewEditProposal = (page: any, proposedBody: string, proposalId: string) => {
            this.navigate({
                view: "editing",
                page: { ...page, body: proposedBody },
                categoryId: page.categoryId,
                folderId: page.folderId ?? null,
                reviewProposalId: proposalId,
            });
        };

        handleEditorSaved = (pageId: string, categoryId: string) => {
            this.bumpTreeVersion();
            this.selectPage(pageId, categoryId);
        };

        handleEditorClose = () => {
            this.navigate({ view: "home" }, { replace: true });
        };

        selectPage = (pageId: string, categoryId: string) => {
            this.navigate({ view: "page", pageId, categoryId });
        };

        selectFolder = (folderId: string, categoryId: string) => {
            this.navigate({ view: "folder", folderId, categoryId });
        };

        selectQuestion = (questionId: string) => {
            this.navigate({ view: "question", questionId });
        };

        renderConfirmModal() {
            const m = this.state.confirmModal;
            if (!m) return null;
            return (
                <ConfirmModal
                    title={m.title}
                    message={m.message}
                    confirmLabel={m.confirmLabel}
                    danger={m.danger}
                    onConfirm={m.onConfirm}
                    onCancel={() => this.setState({ confirmModal: null })}
                />
            );
        }

        renderContent() {
            const { currentView, isAdmin } = this.state;
            const canManage = isAdmin || this.state.role === "editor";

            switch (currentView.view) {
                case "home":
                    return <HomeView isAdmin={isAdmin} categories={this.state.categories} onSelectPage={(pageId, categoryId) => this.selectPage(pageId, categoryId)} onSelectCategory={this.selectCategory} />;
                case "category":
                    return (
                        <CategoryContentView
                            categoryId={currentView.categoryId}
                            categories={this.state.categories}
                            isAdmin={isAdmin}
                            canManage={canManage}
                            currentUserId={this.state.userId}
                            onSelectPage={(pageId) => this.selectPage(pageId, currentView.categoryId)}
                            onSelectFolder={(folderId) => this.selectFolder(folderId, currentView.categoryId)}
                            onSelectQuestion={this.selectQuestion}
                            onCreatePage={() => this.handleCreatePage(currentView.categoryId, null)}
                            onCreateFolder={() => this.openFolderForm(currentView.categoryId)}
                            onEditCategory={(cat) => this.openCategoryForm(cat)}
                            onDeleteCategory={this.handleDeleteCategory}
                            onArchiveCategory={this.handleArchiveCategory}
                            onCategoryChanged={this.reloadCategories}
                            treeVersion={this.state.treeVersion}
                        />
                    );
                case "page":
                    return (
                        <PageView
                            pageId={currentView.pageId}
                            categoryId={currentView.categoryId}
                            categories={this.state.categories}
                            isAdmin={isAdmin}
                            canManage={canManage}
                            userId={this.state.userId}
                            onNavigateCategory={this.selectCategory}
                            onNavigateFolder={this.selectFolder}
                            onDeleted={() => {
                                this.navigate({ view: "category", categoryId: currentView.categoryId }, { replace: true });
                                this.bumpTreeVersion();
                            }}
                            onEdit={this.handleEditPage}
                            onChanged={this.bumpTreeVersion}
                        />
                    );
                case "questions":
                    return (
                        <QuestionsView
                            categories={this.state.categories}
                            currentUserId={this.state.userId}
                            isAdmin={isAdmin}
                            onSelectQuestion={this.selectQuestion}
                        />
                    );
                case "question":
                    return (
                        <QuestionDetailView
                            questionId={currentView.questionId}
                            categories={this.state.categories}
                            currentUserId={this.state.userId}
                            isAdmin={isAdmin}
                            canManage={canManage}
                            onBack={() => this.navigate({ view: "questions" })}
                            onNavigatePage={this.selectPage}
                            onDeleted={() => this.navigate({ view: "questions" }, { replace: true })}
                        />
                    );
                case "drafts":
                    return (
                        <ProfileView
                            currentUserId={this.state.userId}
                            viewUserId={this.state.userId}
                            categories={this.state.categories}
                            onSelectPage={this.selectPage}
                            onSelectCategory={this.selectCategory}
                            onEditPage={this.handleEditPage}
                        />
                    );
                case "profile":
                    return (
                        <ProfileView
                            currentUserId={this.state.userId}
                            viewUserId={currentView.userId}
                            categories={this.state.categories}
                            onSelectPage={this.selectPage}
                            onSelectCategory={this.selectCategory}
                            onEditPage={this.handleEditPage}
                        />
                    );
                case "km":
                    return (
                        <KnowledgeManagementView
                            categories={this.state.categories}
                            isAdmin={isAdmin}
                            canManage={canManage}
                            onSelectPage={this.selectPage}
                            onSelectCategory={this.selectCategory}
                            onReviewEdit={this.handleReviewEditProposal}
                        />
                    );
                case "archive":
                    return (
                        <ArchivedView
                            categories={this.state.categories}
                            onRestored={() => {
                                this.reloadCategories();
                                this.bumpTreeVersion();
                            }}
                        />
                    );
                case "templates":
                    return (
                        <TemplatesView
                            categories={this.state.categories}
                            isAdmin={isAdmin}
                            onSelectPage={this.selectPage}
                            onPageCreated={(p) => this.handleEditPage(p)}
                        />
                    );
                case "search":
                    return (
                        <SearchView
                            query={currentView.query}
                            type={currentView.type}
                            cats={currentView.cats}
                            categories={this.state.categories}
                            onSelectPage={this.selectPage}
                            onSelectQuestion={this.selectQuestion}
                            onSelectCategory={this.selectCategory}
                            onChangeFilters={(type, cats) => this.navigate({ view: "search", query: currentView.query, type, cats }, { replace: true })}
                        />
                    );
                case "folder":
                    return (
                        <FolderView
                            folderId={currentView.folderId}
                            categoryId={currentView.categoryId}
                            categories={this.state.categories}
                            isAdmin={isAdmin}
                            canManage={canManage}
                            currentUserId={this.state.userId}
                            onSelectPage={this.selectPage}
                            onCreatePage={this.handleCreatePage}
                            onEditFolder={this.openEditFolderForm}
                            onDeleteFolder={this.handleDeleteFolder}
                            onSelectCategory={this.selectCategory}
                            treeVersion={this.state.treeVersion}
                        />
                    );
                default:
                    return null;
            }
        }

        render() {
            if (this.state.loading) {
                return <div className="m-layout"><div className="m-loading"><CustomLoading /></div></div>;
            }

            const { currentView } = this.state;
            const canManage = this.state.isAdmin || this.state.role === "editor";

            // full-page editor takes over the content area
            if (currentView.view === "editing") {
                return (
                    <ManualsDndWrap onDragStart={this.handleDragStart} onDragEnd={this.handleDragEnd} onDragCancel={this.handleDragCancel} activeDrag={this.state.activeDrag}>
                        <div className="m-layout">
                            <Sidebar
                                categories={this.state.categories}
                                expandedCategories={this.state.expandedCategories}
                                userId={this.state.userId}
                                currentView={this.state.currentView}
                                isAdmin={this.state.isAdmin}
                                canManage={canManage}
                                onNavigate={this.navigate}
                                onToggleCategory={this.toggleCategory}
                                onSelectCategory={this.selectCategory}
                                onCreateCategory={() => this.openCategoryForm(null)}
                                onEditCategory={(cat) => this.openCategoryForm(cat)}
                                onDeleteCategory={this.handleDeleteCategory}
                                onCreateFolder={this.openFolderForm}
                                onEditFolder={this.openEditFolderForm}
                                onDeleteFolder={this.handleDeleteFolder}
                                onSelectPage={this.selectPage}
                                onCreatePage={this.handleCreatePage}
                                onSelectFolder={this.selectFolder}
                                treeVersion={this.state.treeVersion}
                            />
                            <div className="m-content m-content-editor">
                                <PageEditor
                                    key={currentView.page?.id || `new-${currentView.categoryId}-${currentView.folderId || "root"}`}
                                    page={currentView.page}
                                    categoryId={currentView.categoryId}
                                    folderId={currentView.folderId}
                                    categories={this.state.categories}
                                    reviewProposalId={currentView.reviewProposalId}
                                    onSave={this.handleEditorSaved}
                                    onClose={this.handleEditorClose}
                                />
                            </div>

                            {this.state.categoryFormOpen && (
                                <CategoryForm
                                    category={this.state.categoryFormTarget}
                                    currentUserId={this.state.userId}
                                    onSave={this.handleCategoryFormSaved}
                                    onCancel={this.closeCategoryForm}
                                />
                            )}
                            {this.state.folderFormOpen && (
                                <FolderForm
                                    folder={this.state.folderFormTarget}
                                    categoryId={this.state.folderFormCategoryId!}
                                    onSave={this.handleFolderFormSaved}
                                    onCancel={this.closeFolderForm}
                                />
                            )}
                            {this.renderConfirmModal()}
                        </div>
                    </ManualsDndWrap>
                );
            }

            return (
                <ManualsDndWrap onDragStart={this.handleDragStart} onDragEnd={this.handleDragEnd} onDragCancel={this.handleDragCancel} activeDrag={this.state.activeDrag}>
                    <div className="m-layout">
                        <Sidebar
                            categories={this.state.categories}
                            expandedCategories={this.state.expandedCategories}
                            userId={this.state.userId}
                            currentView={this.state.currentView}
                            isAdmin={this.state.isAdmin}
                            canManage={canManage}
                            onNavigate={this.navigate}
                            onToggleCategory={this.toggleCategory}
                            onSelectCategory={this.selectCategory}
                            onCreateCategory={() => this.openCategoryForm(null)}
                            onEditCategory={(cat) => this.openCategoryForm(cat)}
                            onDeleteCategory={this.handleDeleteCategory}
                            onCreateFolder={this.openFolderForm}
                            onEditFolder={this.openEditFolderForm}
                            onDeleteFolder={this.handleDeleteFolder}
                            onSelectPage={this.selectPage}
                            onCreatePage={this.handleCreatePage}
                            onSelectFolder={this.selectFolder}
                            treeVersion={this.state.treeVersion}
                        />
                        <div className="m-content">
                            <form className="m-search-bar" onSubmit={this.handleSearch}>
                                <div className="m-search-input-wrap" ref={this.searchWrapRef}>
                                    <Search size={16} className="m-search-icon" />
                                    <input
                                        type="text"
                                        className="m-search-input"
                                        placeholder="Search pages, questions, documents..."
                                        value={this.state.searchQuery}
                                        onChange={e => this.setState({ searchQuery: e.target.value, searchOpen: true })}
                                        onFocus={this.openSearch}
                                    />
                                    {this.state.searchOpen && (
                                        <SearchDropdown
                                            query={this.state.searchQuery}
                                            categories={this.state.categories}
                                            onClose={this.closeSearch}
                                            onAdvanced={() => {
                                                const q = this.state.searchQuery.trim();
                                                this.closeSearch();
                                                if (q) this.navigate({ view: "search", query: q });
                                            }}
                                            onAskQuestion={() => {
                                                this.closeSearch();
                                                this.setState({ askQuestionOpen: true });
                                            }}
                                            onSelectPage={(pageId, categoryId) => {
                                                this.closeSearch();
                                                this.selectPage(pageId, categoryId);
                                            }}
                                            onSelectQuestion={(questionId) => {
                                                this.closeSearch();
                                                this.navigate({ view: "question", questionId });
                                            }}
                                            onSelectCategory={(categoryId) => {
                                                this.closeSearch();
                                                this.selectCategory(categoryId);
                                            }}
                                        />
                                    )}
                                </div>
                            </form>
                            {(this.state.currentView.view === "category" || this.state.currentView.view === "folder" || this.state.currentView.view === "home" || this.state.currentView.view === "page" || this.state.currentView.view === "drafts" || this.state.currentView.view === "profile" || this.state.currentView.view === "questions" || this.state.currentView.view === "question" || this.state.currentView.view === "km" || this.state.currentView.view === "templates" || this.state.currentView.view === "search")
                                ? this.renderContent()
                                : <div className="m-main">{this.renderContent()}</div>}
                        </div>

                        {this.state.categoryFormOpen && (
                            <CategoryForm
                                category={this.state.categoryFormTarget}
                                currentUserId={this.state.userId}
                                onSave={this.handleCategoryFormSaved}
                                onCancel={this.closeCategoryForm}
                            />
                        )}

                        {this.state.folderFormOpen && (
                            <FolderForm
                                folder={this.state.folderFormTarget}
                                categoryId={this.state.folderFormCategoryId!}
                                onSave={this.handleFolderFormSaved}
                                onCancel={this.closeFolderForm}
                            />
                        )}
                        {this.state.askQuestionOpen && (
                            <QuestionForm
                                categories={this.state.categories}
                                onSave={(created) => {
                                    this.setState({ askQuestionOpen: false, searchQuery: "" });
                                    if (created) this.navigate({ view: "question", questionId: created.id });
                                }}
                                onCancel={() => this.setState({ askQuestionOpen: false })}
                            />
                        )}
                        {this.renderConfirmModal()}
                    </div>
                </ManualsDndWrap>
            );
        }
    }
);

function ManualsDndWrap({ onDragStart, onDragEnd, onDragCancel, activeDrag, children }: {
    onDragStart: (e: DragStartEvent) => void;
    onDragEnd: (e: DragEndEvent) => void;
    onDragCancel: () => void;
    activeDrag: { type: "page"; label: string } | null;
    children: React.ReactNode;
}) {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
            {children}
            <DragOverlay dropAnimation={null}>
                {activeDrag ? (
                    <div className="m-drag-overlay">
                        <FileText size={14} />
                        <span className="m-drag-overlay-label">{activeDrag.label || "Page"}</span>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}

function PlaceholderView({ title }: { title: string }) {
    return (
        <div className="m-empty-state">
            <div style={{ fontSize: 16, fontWeight: 500 }}>{title}</div>
            <div className="m-empty-hint">Coming soon</div>
        </div>
    );
}
